// src/pages/Enseignants/Palmares/CreatePalmaresModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSave, FaTimes, FaCopy, FaSearch } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { addPalmares } from "./../../../services/palmaresServices";
import { getAllAdherentsList } from "../../../services/adherentServices";
import { getAllEvents } from "../../../services/eventServices";

/* Switch "Afficher sur l'accueil" */
function VisibilitySwitch({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer select-none">
      <button
        type="button"
        onClick={onChange}
        role="switch"
        aria-checked={checked}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
        title={checked ? "Affiché sur l’accueil" : "Masqué de l’accueil"}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
      {label ? (
        <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
      ) : null}
    </label>
  );
}

const defaultForm = () => ({
  adherent_palmares_id: "",
  event_palmares_id: "",   // optionnel
  date_palmares: "",
  titre_palmares: "",
  resultats_palmares: "",
  classement_palmares: "",
  observations_palmares: "",
  is_active: true,
});

export default function CreatePalmaresModal({ isOpen, onClose, refreshList }) {
  const [form, setForm] = useState(defaultForm());

  const [adherents, setAdherents] = useState([]);
  const [loadingAdherents, setLoadingAdherents] = useState(false);

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventQuery, setEventQuery] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // Reset + fetch
  useEffect(() => {
    if (!isOpen) return;

    setForm(defaultForm());
    setEventQuery("");

    (async () => {
      try {
        setLoadingAdherents(true);
        const data = await getAllAdherentsList();
        setAdherents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des adhérents", error);
        toast.error("Impossible de charger la liste des adhérents.");
      } finally {
        setLoadingAdherents(false);
      }
    })();

    (async () => {
      try {
        setLoadingEvents(true);
        const evts = await getAllEvents();
        const list = Array.isArray(evts) ? evts : [];
        // tri par date desc quand possible
        const normDate = (e) =>
          e?.date ||
          e?.date_event ||
          e?.jour ||
          e?.datetime ||
          e?.start ||
          null;
        list.sort((a, b) => {
          const da = new Date(normDate(a));
          const db = new Date(normDate(b));
          const va = isNaN(da) ? 0 : da.getTime();
          const vb = isNaN(db) ? 0 : db.getTime();
          return vb - va;
        });
        setEvents(list);
      } catch (error) {
        console.error("Erreur lors de la récupération des événements", error);
        toast.error("Impossible de charger la liste des événements.");
      } finally {
        setLoadingEvents(false);
      }
    })();
  }, [isOpen]);

  // Fermer via ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const sortedMembers = useMemo(() => {
    return [...adherents].sort((a, b) =>
      `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, "fr", {
        sensitivity: "base",
      })
    );
  }, [adherents]);

  const formatEventLabel = (e) => {
    const name =
      e?.titre ||
      e?.title ||
      e?.nom ||
      e?.name ||
      e?.event_nom ||
      "Événement";
    const rawDate =
      e?.date || e?.date_event || e?.jour || e?.datetime || e?.start || null;
    let dateStr = "";
    if (rawDate) {
      const d = new Date(rawDate);
      if (!isNaN(d)) dateStr = d.toLocaleDateString("fr-FR");
    }
    const place = e?.lieu || e?.ville || e?.location || "";
    return [name, dateStr, place].filter(Boolean).join(" — ");
  };

  const filteredEvents = useMemo(() => {
    const q = eventQuery.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => formatEventLabel(e).toLowerCase().includes(q));
  }, [events, eventQuery]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.adherent_palmares_id) {
      toast.warn("Sélectionnez un adhérent.");
      return false;
    }
    if (!form.date_palmares) {
      toast.warn("La date est requise.");
      return false;
    }
    if (!form.titre_palmares.trim()) {
      toast.warn("Le titre est requis.");
      return false;
    }
    return true;
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validate()) return;
      setSubmitting(true);
      try {
        // Remarque : si event_palmares_id === "" on l’envoie tel quel ;
        // côté API, idéalement vous normalisez "" -> null.
        await addPalmares(form);
        toast.success("Palmarès ajouté avec succès !");
        refreshList?.();
        onClose?.();
      } catch (error) {
        console.error("Erreur lors de la création du palmarès", error);
        toast.error("Erreur lors de la création du palmarès.");
      } finally {
        setSubmitting(false);
      }
    },
    [form, onClose, refreshList]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Ajouter un palmarès
          </h3>
          <p className="mt-1 text-white/90 text-xs text-center">
            Sélectionnez l’adhérent, associez un événement (optionnel), puis
            renseignez le titre et les infos clés.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
          {/* Adhérent + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Adhérent
              </label>
              <select
                name="adherent_palmares_id"
                value={form.adherent_palmares_id}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
              >
                <option value="">
                  {loadingAdherents ? "Chargement…" : "— Sélectionnez un adhérent —"}
                </option>
                {!loadingAdherents &&
                  sortedMembers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.nom || "").toUpperCase()} {a.prenom || ""}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Date
              </label>
              <input
                type="date"
                name="date_palmares"
                value={form.date_palmares}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
              />
            </div>
          </div>

          {/* Evénement (optionnel) */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-2">
              Événement (optionnel)
            </label>

            {/* Recherche */}
            <div className="relative mb-2">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={eventQuery}
                onChange={(e) => setEventQuery(e.target.value)}
                placeholder="Rechercher un événement par nom, date, lieu…"
                className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* Select événements */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <select
                name="event_palmares_id"
                value={form.event_palmares_id || ""}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">
                  {loadingEvents ? "Chargement…" : "— Aucun événement —"}
                </option>
                {!loadingEvents &&
                  filteredEvents.map((e) => (
                    <option key={e.id} value={e.id}>
                      {formatEventLabel(e)}
                    </option>
                  ))}
              </select>

              {/* Action copier le titre */}
              <button
                type="button"
                onClick={() => {
                  const evt = events.find((e) => String(e.id) === String(form.event_palmares_id));
                  if (!evt) {
                    toast.info("Sélectionnez un événement d’abord.");
                    return;
                  }
                  const name =
                    evt?.titre || evt?.title || evt?.nom || evt?.name || evt?.event_nom || "";
                  if (!name) {
                    toast.info("Événement sans nom exploitable.");
                    return;
                  }
                  setForm((f) => ({
                    ...f,
                    titre_palmares: f.titre_palmares?.trim()
                      ? f.titre_palmares
                      : name,
                  }));
                  if (!form.titre_palmares?.trim()) {
                    toast.success("Titre renseigné depuis l’événement.");
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <FaCopy /> Copier dans le titre
              </button>
            </div>

            {/* “Pill” de confirmation */}
            {form.event_palmares_id ? (
              <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200 px-3 py-1 text-xs">
                Événement sélectionné
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, event_palmares_id: "" }))}
                  className="ml-1 underline"
                  title="Retirer l’événement"
                >
                  Retirer
                </button>
              </div>
            ) : null}
          </div>

          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Titre de l’événement / compétition
            </label>
            <input
              type="text"
              name="titre_palmares"
              value={form.titre_palmares}
              onChange={onChange}
              placeholder="Ex : 1er au Tournoi Régional"
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            />
          </div>

          {/* Résultats / Classement */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Résultats (optionnel)
              </label>
              <input
                type="text"
                name="resultats_palmares"
                value={form.resultats_palmares}
                onChange={onChange}
                placeholder="Ex : 3 combats gagnés, 1 perdu"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Classement (optionnel)
              </label>
              <input
                type="text"
                name="classement_palmares"
                value={form.classement_palmares}
                onChange={onChange}
                placeholder="Ex : 2e"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </div>

          {/* Observations */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Observations (optionnel)
            </label>
            <textarea
              name="observations_palmares"
              value={form.observations_palmares}
              onChange={onChange}
              placeholder="Notes, contexte, remarques…"
              rows={4}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>

          {/* Visibilité */}
          <div className="pt-1">
            <VisibilitySwitch
              checked={!!form.is_active}
              onChange={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
              label="Afficher ce palmarès sur la page d’accueil"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
              disabled={submitting}
            />
            <Button
              text={submitting ? "En cours…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${submitting ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
